import { useFormState } from "./useStateFormStore";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import resolveParams from '../../../../../../common/model/utilities/resolveParams'
import useApi from "../../../../hooks/useApi";
import useLocale from "../../../../hooks/useLocale";
import useTranslation from "../../../../hooks/useTranslation";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { getLoadUrls, mapResultsToState } from "../../../../utils/api/getLoadUrls";
import transferSettings, { getSettingsKey } from "../../../pageView/processSettings";
import findSuggestions, { amountOf, combineRows } from "../../../../../../common/utils/greedy/findGreedy";
import resolveModalForm from "../../../../utils/model/resolveModalForm";

import SkeletonLoader from "../../../generic/SkeletonLoader";
import StateFormElem from "./StateFormElem";
import { media } from "../../../../styles/media";

const SuggestionsContainer = styled.div`
    margin-top: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 1rem;

    ${media.notPhone} {
        max-height: 35vh;
    }
  
    overflow-y: scroll;
`

const SelectionTotal = styled.div`
    margin-top: 1rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
`

// Tolerance below which a bank transaction counts as used up
const USED_UP = 0.01;

const StateFormView = ({ item, parentId, onUpdate, validated, useStore, section, lineId, path, onClose }) => {

    const { m } = useTranslation();
    const { lf } = useLocale();
    const [suggestions, setSuggestions] = useState(null);
    // The ids of the rows chosen in the list above — one, or several when that
    // list allows it (`singleSelect: false`)
    const [selected, setSelected] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- State and Config Setup ---
    const loads = item.load || [];
    const targets = item.targets || [];

    // Local form state
    const [state, setState] = useFormState((s) => s);

    // Derived resource URLs using parentId prop
    const resources = getLoadUrls(loads, parentId) || [];

    // API Function
    const { getMe, postMe } = useApi();
    const queryClient = useQueryClient();
    const getSettingsData = (key) => queryClient.getQueryData([key])?.data;

    const settingsKeys = item.loadSettings || [];
    const settingsQueries = useQueries({
        queries: settingsKeys.map(entry => {
            const key = getSettingsKey(entry)
            return { queryKey: [key], queryFn: () => getMe(key), staleTime: Infinity }
        })
    });
    const settingsReady = settingsKeys.length === 0 || settingsQueries.every(q => q.isSuccess);

    const groups = resolveModalForm(item).elements;
    const suggestionConfig = item.suggestions || {};

    // --- Direct API Fetch on Mount ---
    useEffect(() => {
        if (!settingsReady) return;

        // Reset state on every mount
        setState({ loaded: false });
        setSuggestions(null);
        setSelected([]);
        setIsLoading(true);
        setError(null);

        // If no resources, mark as loaded immediately
        if (resources.length === 0) {
            setIsLoading(false);
            setState({ loaded: true });
            return;
        }

        // Fetch all resources directly
        const fetchData = async () => {
            try {
                const promises = resources.map(endpoint => getMe(endpoint));
                const responses = await Promise.all(promises);

                // Map results to state. A `single` load reads one record by
                // id; it is wrapped as a list, which is what a target is.
                const results = responses.map((data, index) => {
                    const payload = data?.data || data;
                    const wrap = loads[index]?.single && payload && !Array.isArray(payload);
                    return { isSuccess: true, data: { data: wrap ? [payload] : payload } };
                });

                const AllResults = mapResultsToState(targets, results);
                const enriched = settingsKeys.length > 0
                    ? transferSettings(AllResults, getSettingsData, settingsKeys)
                    : AllResults;
                setState({ ...enriched, loaded: true });
                setIsLoading(false);
            } catch (err) {
                console.error("Error loading StateForm data:", err);
                setError(err);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [settingsReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Suggestions Effect ---
    useEffect(() => {
        if (state.loaded) {
            const primaryData = targets[0] ? state[targets[0]] : null;
            const secondaryData = targets[1] ? state[targets[1]] : null;
            
            if (Array.isArray(primaryData) && Array.isArray(secondaryData)) {
                // `suggestionOptions` names the fields and signs each side is
                // compared on, when they are not an invoicePayment's.
                const suggs = findSuggestions(primaryData, secondaryData, 30, 95, item.suggestionOptions);
                setSuggestions(suggs);
            } else {
                setSuggestions(null);
            }
        }
    }, [state.loaded, state, targets]);

    // The selected rows, oldest first — the order they are matched in, so when
    // the transaction falls short it is the latest one left part-paid
    const primaryRows = Array.isArray(state?.[targets[0]]) ? state[targets[0]] : [];
    const [dateField] = item.suggestionOptions?.dateFields ?? ['paymentDate'];
    const selectedRows = useMemo(() => primaryRows
        .filter(row => selected.includes(row?.id))
        .sort((a, b) => new Date(a?.[dateField]) - new Date(b?.[dateField])),
        [primaryRows, selected, dateField]);

    /*
     * The transactions offered for the selection. One row reads the search run
     * over every row above. Several are summed into one row and searched again,
     * on their own: a transfer paying three instalments matches their total,
     * not any one of them, and the search over all rows would have handed it to
     * whichever single instalment it happened to fit.
     */
    const activeSuggestions = useMemo(() => {
        if (selected.length === 0) return null;
        if (selected.length === 1) return suggestions?.[selected[0]] ?? null;

        const secondaryData = state?.[targets[1]];
        if (selectedRows.length === 0 || !Array.isArray(secondaryData)) return null;
        const combined = combineRows(selectedRows, item.suggestionOptions);
        return findSuggestions([combined], secondaryData, 30, 95, item.suggestionOptions)[combined.id] ?? null;
    }, [selected, suggestions, selectedRows, state, targets, item.suggestionOptions]);

    // What the selected rows add up to, in their own currency, for the line
    // above the suggestions
    const selectedTotal = selectedRows.reduce((sum, row) => sum + (Number(row?.amountDue ?? row?.paymentAmount) || 0), 0);
    const selectedCurrency = selectedRows[0]?.paymentCurrency ?? selectedRows[0]?.currency ?? '';

    // --- Render Logic ---
    if (isLoading) {
        return <SkeletonLoader config={['L', 'F', 'F', 'L', 'F', 'F', 'F', 'F', 'F', 'F']} />;
    }

    if (error) {
        return <div>Error loading data. Please try again.</div>;
    }

    // --- Action handling for suggestion SelectList ---
    const handleSuggestionAction = async (selectedItem, actionConfig) => {
        if (!selectedItem || !actionConfig?.endpoint) return;

        try {
            // The endpoint and the body, declared.
            //
            // Both were built here: `:id` substituted by hand, and five fields
            // written into this function — two of them (`origin`, `destination`)
            // read straight off `targets`, which is this element's own config and
            // so belongs in the action as literals rather than travelling through
            // context (33.2 step 9).
            //
            // `item` is the suggestion pressed and `selection` the row chosen in
            // the list above, which is the one thing the process-view vocabulary
            // had no name for: `item`/`record`/`input` assume a single work item,
            // and this action has two rows in play at once. `selection` carries
            // the whole row where one can be found, so a later param can read
            // more than its id.
            //
            // With several rows selected the action runs once per row, oldest
            // first, one after the other: each match reads what the last one
            // left on the transaction. It stops when the transaction is used
            // up — a further match would apply nothing — and at the first
            // failure, keeping the matches already made.
            const rows = selectedRows.length > 0 ? selectedRows : selected.map(id => ({ id }));
            // What is left on the transaction, in base currency: its running
            // total once a match has written one, its amount before that
            let left = Math.abs(selectedItem.amountLeftBaseCurrency ?? amountOf(selectedItem, 1, item.suggestionOptions));

            for (const row of rows) {
                // An amount that cannot be read never stops it; the server caps each match anyway
                if (Number.isFinite(left) && left <= USED_UP) break;

                const context = {
                    item: selectedItem,
                    selection: row,
                    record: state,
                };

                const endpoint = resolveParams(actionConfig.endpoint, context);
                const payload = resolveParams(actionConfig.body, context);

                const result = await postMe(endpoint, payload);
                if (result?.status === 'error' || result?.success === false) {
                    console.error('Match failed; the rows before it stay matched', row.id, result);
                    break;
                }
                left -= Math.abs(amountOf(row, 0, item.suggestionOptions)) || 0;
            }

            if (typeof onUpdate === 'function') {
                onUpdate();
            }
            
            if (typeof onClose === 'function') {
                onClose();
            }
        } catch (err) {
            console.error('Error while executing suggestion action', err);
        }
    };

    return (
        <>
            {Array.isArray(groups) && groups.map((elem, i) => {
                return (
                    <StateFormElem
                        elem={elem}
                        validated={validated}
                        useStore={useStore}
                        useFormState={useFormState}
                        section={elem}
                        key={`sfe-${i}`}
                        lineId={lineId}
                        path={path}
                        setSelected={setSelected}
                        data={state[elem?.source] ?? null}
                    />
                );
            })}
            {selectedRows.length > 1 && (
                <SelectionTotal data-testid="stateFormSelectionTotal">
                    {m('stateForm.selection.total', [selectedRows.length, `${lf(selectedTotal, 2, 2)} ${selectedCurrency}`.trim()])}
                </SelectionTotal>
            )}
            {activeSuggestions && (
                <SuggestionsContainer>
                    <StateFormElem
                        elem={suggestionConfig}
                        validated={validated}
                        useStore={useStore}
                        useFormState={useFormState}
                        section={suggestionConfig}
                        lineId={lineId}
                        path={path}
                        setSelected={setSelected}
                        data={activeSuggestions}
                        onAction={handleSuggestionAction}
                    />
                </SuggestionsContainer>
            )}
        </>
    )
}

export default StateFormView;
